import { gql } from '@apollo/client'
import { useAppSetting, useDocument, useUser } from '@ztechapps/common.hook'

const getPublicProductSpecificationsQuery = gql`
  query getPublicProductSpecifications($filter: JSON) {
    getPublicProductSpecifications(filter: $filter)
  }
`

export type UseProductSpecificationValueProps = {
  appSettingKey: string
}

export type UseProductSpecificationValueResult = {
  value: string | undefined
  loading: boolean
}

/**
 * The JSON of the product specifications: a logged-out visitor reads the public entries through the anonymous
 * `getPublicProductSpecifications` query, a logged-in user reads the full `appSettingKey` row.
 * Both sources are called on every render; `skip` and the undefined key switch the inactive one off.
 */
export const useProductSpecificationValue = ({
  appSettingKey,
}: UseProductSpecificationValueProps): UseProductSpecificationValueResult => {
  const { loggedIn } = useUser()

  const { setting, loading: settingLoading } = useAppSetting({ appSettingKey: loggedIn ? appSettingKey : undefined })
  // The empty filter keeps `useDocument` from skipping the read; `loggedIn` skips it. `T` also types the filter, so a
  // bare string result is read untyped.
  const { document, loading: publicLoading } = useDocument({
    query: getPublicProductSpecificationsQuery,
    filter: {},
    options: { queryOptions: { skip: loggedIn } },
  })

  return loggedIn
    ? { value: setting?.value, loading: settingLoading }
    : { value: document as string | undefined, loading: publicLoading }
}
